import { useEffect, useRef } from "react";
import type { CheckoutSession } from "@/types";
import { emitPageUnloadTracking } from "@/shared/tracking/analytics";

/**
 * Tracks page unload events for analytics.
 *
 * Uses a dual-listener strategy (`beforeunload` + `pagehide`) because:
 * - `beforeunload` fires reliably on tab close but NOT on mobile Safari navigation.
 * - `pagehide` fires on navigation and bfcache eviction but `persisted` flag
 *   must be checked to distinguish real unloads from bfcache suspensions.
 *
 * The 500ms guard (`shouldAllowPageUnload`) prevents false unload events from
 * rapid page transitions during initial load/redirect chains.
 */
export function usePageUnloadTracking(session?: CheckoutSession): void {
  const pageLoadTimeRef = useRef<number | null>(null);
  const nextUrlRef = useRef<string | null>(null);
  const isLinkClickRef = useRef<boolean>(false);
  const hasEmittedUnloadEventRef = useRef<boolean>(false);
  const beforeUnloadFiredRef = useRef<boolean>(false);
  const sessionRef = useRef<CheckoutSession | undefined>(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    if (pageLoadTimeRef.current === null) {
      pageLoadTimeRef.current = Date.now();
    }
  }, []);

  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");
      if (anchor?.href) {
        try {
          const anchorUrl = new URL(anchor.href, window.location.href);
          const currentUrl = new URL(window.location.href);

          if (anchorUrl.origin !== currentUrl.origin) {
            nextUrlRef.current = anchor.href;
            isLinkClickRef.current = true;
          }
        } catch {
          // Invalid URL, ignore
        }
      }
    };

    const shouldAllowPageUnload = (): boolean => {
      if (!pageLoadTimeRef.current) return false;
      return Date.now() - pageLoadTimeRef.current >= 500;
    };

    const handleBeforeUnload = (): void => {
      if (hasEmittedUnloadEventRef.current) return;

      beforeUnloadFiredRef.current = true;
      hasEmittedUnloadEventRef.current = true;

      const unloadReason = isLinkClickRef.current ? "link_click" : "tab_close";
      emitPageUnloadTracking(
        unloadReason,
        nextUrlRef.current,
        sessionRef.current,
      );
    };

    const handlePageHide = (e: PageTransitionEvent): void => {
      if (hasEmittedUnloadEventRef.current) return;

      const isRealUnload =
        beforeUnloadFiredRef.current ||
        !e.persisted ||
        (isLinkClickRef.current && shouldAllowPageUnload());

      if (!isRealUnload) return;

      hasEmittedUnloadEventRef.current = true;

      const unloadReason = isLinkClickRef.current
        ? "link_click"
        : e.persisted
          ? "navigation"
          : "tab_close";

      emitPageUnloadTracking(
        unloadReason,
        nextUrlRef.current,
        sessionRef.current,
      );
    };

    document.addEventListener("click", handleLinkClick, true);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("click", handleLinkClick, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);
}
